import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertCircle, CalendarDays, DollarSign, FileText, Paperclip, Pencil, Plus, ReceiptText, Search, Sheet, Trash2, WalletCards, X } from 'lucide-react';
import { Header } from '../components/Layout/Header';
import { Button } from '../components/common/Button';
import { Modal } from '../components/common/Modal';
import { AttachmentPanel } from '../components/common/AttachmentPanel';
import { Pagination } from '../components/common/Pagination';
import { ProjectForm } from '../components/Projects/ProjectForm';
import { useProjectStore } from '../store/projectStore';
import { financeApi, projectsApi } from '../services/api';
import { BudgetLine, ExpenseStatus, FinanceExpense, FinanceRegime, FinanceSummary, FinanceSupplier, PaymentAccount, Project } from '../types';
import { formatBRLCurrency, formatBRLCurrencyInput, parseBRLCurrency } from '../utils/currency';

const statusLabels: Record<ExpenseStatus, string> = {
  open: 'Em aberto',
  paid: 'Pago',
  cancelled: 'Cancelado',
};

const statusStyles: Record<ExpenseStatus, string> = {
  open: 'bg-amber-50 text-amber-700 ring-amber-200',
  paid: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  cancelled: 'bg-gray-100 text-gray-600 ring-gray-200',
};

const reportMetricLabels = {
  planned: 'Orcado',
  total: 'Comprometido',
  paid: 'Pago',
  open: 'Em aberto',
  overdue: 'Atrasado',
  balance: 'Saldo previsto',
};

interface ExpenseFormData {
  project_id: string;
  budget_line_id: string;
  supplier_name: string;
  document_type: string;
  description: string;
  issued_at: string;
  due_at: string;
  paid_at: string;
  payment_account_name: string;
  amount: string;
  notes: string;
  status: ExpenseStatus;
}

const today = new Date().toISOString().slice(0, 10);
const monthStart = `${today.slice(0, 8)}01`;
const DEFAULT_PAGE_SIZE = 10;

function emptyExpenseForm(projectId = ''): ExpenseFormData {
  return {
    project_id: projectId,
    budget_line_id: '',
    supplier_name: '',
    document_type: 'Fatura',
    description: '',
    issued_at: today,
    due_at: '',
    paid_at: '',
    payment_account_name: '',
    amount: '',
    notes: '',
    status: 'open',
  };
}

function toNumber(value: number | string | undefined | null): number {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function expenseToForm(expense: FinanceExpense): ExpenseFormData {
  return {
    project_id: expense.project_id,
    budget_line_id: expense.budget_line_id || '',
    supplier_name: expense.supplier_name,
    document_type: expense.document_type,
    description: expense.description,
    issued_at: dateOnly(expense.issued_at),
    due_at: dateOnly(expense.due_at),
    paid_at: dateOnly(expense.paid_at),
    payment_account_name: expense.payment_account_name || '',
    amount: formatBRLCurrencyInput(toNumber(expense.amount).toFixed(2)),
    notes: expense.notes || '',
    status: expense.status,
  };
}

function dateOnly(value?: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function displayDate(value?: string | null): string {
  if (!value) return '-';
  const [year, month, day] = dateOnly(value).split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function fileDate(value: string): string {
  return value ? value.replace(/-/g, '') : 'sem-data';
}

function moneyCell(value: number | string | undefined | null): string {
  return escapeHtml(formatBRLCurrency(toNumber(value)));
}

function tableRows(rows: string[][]): string {
  return rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('');
}

function tableHead(columns: string[]): string {
  return `<thead><tr>${columns.map(column => `<th>${escapeHtml(column)}</th>`).join('')}</tr></thead>`;
}

function buildFinanceReportHtml({
  expenses,
  summary,
  filters,
  generatedAt,
}: {
  expenses: FinanceExpense[];
  summary: FinanceSummary | null;
  filters: {
    project: string;
    regime: FinanceRegime;
    startDate: string;
    endDate: string;
  };
  generatedAt: string;
}) {
  const totals = summary?.totals;
  const planned = toNumber(totals?.planned_amount);
  const total = toNumber(totals?.total_amount);
  const paid = toNumber(totals?.paid_amount);
  const open = toNumber(totals?.open_amount);
  const overdue = toNumber(totals?.overdue_amount);
  const balance = planned - total;
  const period = `${displayDate(filters.startDate)} a ${displayDate(filters.endDate)}`;
  const regimeLabel = filters.regime === 'cash' ? 'Caixa' : 'Competencia';

  const summaryRows = tableRows([
    [escapeHtml(reportMetricLabels.planned), moneyCell(planned)],
    [escapeHtml(reportMetricLabels.total), moneyCell(total)],
    [escapeHtml(reportMetricLabels.paid), moneyCell(paid)],
    [escapeHtml(reportMetricLabels.open), moneyCell(open)],
    [escapeHtml(reportMetricLabels.overdue), moneyCell(overdue)],
    [escapeHtml(reportMetricLabels.balance), moneyCell(balance)],
  ]);

  const expenseRows = expenses.length > 0
    ? tableRows(expenses.map(expense => [
      escapeHtml(displayDate(expense.issued_at)),
      escapeHtml(expense.supplier_name),
      escapeHtml(expense.description),
      escapeHtml(expense.management_code ? `${expense.management_code} - ${expense.project_name || ''}` : expense.project_name || ''),
      escapeHtml(expense.budget_line_name || '-'),
      escapeHtml(displayDate(expense.due_at)),
      moneyCell(expense.amount),
      escapeHtml(displayDate(expense.paid_at)),
      escapeHtml(expense.payment_account_name || '-'),
      escapeHtml(statusLabels[expense.status]),
      escapeHtml(expense.document_type),
      escapeHtml(expense.notes || ''),
    ]))
    : '<tr><td colspan="12">Nenhuma despesa encontrada para os filtros atuais.</td></tr>';

  const projectRows = summary?.byProject.length
    ? tableRows(summary.byProject.map(project => {
      const projectPlanned = toNumber(project.planned_amount);
      const projectTotal = toNumber(project.total_amount);
      return [
        escapeHtml(project.management_code ? `${project.management_code} - ${project.project_name}` : project.project_name),
        moneyCell(projectPlanned),
        moneyCell(projectTotal),
        moneyCell(project.paid_amount),
        moneyCell(project.open_amount),
        moneyCell(projectPlanned - projectTotal),
      ];
    }))
    : '<tr><td colspan="6">Nenhum projeto encontrado.</td></tr>';

  const budgetLineRows = summary?.byBudgetLine.length
    ? tableRows(summary.byBudgetLine.map(line => {
      const linePlanned = toNumber(line.planned_amount);
      const lineTotal = toNumber(line.total_amount);
      return [
        escapeHtml(line.project_name),
        escapeHtml(line.budget_line_name),
        moneyCell(linePlanned),
        moneyCell(lineTotal),
        moneyCell(line.paid_amount),
        moneyCell(linePlanned - lineTotal),
      ];
    }))
    : '<tr><td colspan="6">Nenhuma linha orcamentaria encontrada.</td></tr>';

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Relatorio financeiro</title>
  <style>
    body { color: #323338; font-family: Arial, sans-serif; margin: 24px; }
    h1 { font-size: 22px; margin: 0 0 8px; }
    h2 { border-bottom: 1px solid #d0d4e4; font-size: 15px; margin: 24px 0 8px; padding-bottom: 6px; }
    p { color: #676879; font-size: 12px; margin: 2px 0; }
    table { border-collapse: collapse; font-size: 11px; margin-top: 8px; width: 100%; }
    th { background: #f7f9ff; color: #323338; font-weight: 700; text-align: left; }
    th, td { border: 1px solid #d0d4e4; padding: 6px; vertical-align: top; }
    @media print {
      body { margin: 12mm; }
      h2 { break-after: avoid; page-break-after: avoid; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; page-break-after: auto; }
    }
  </style>
</head>
<body>
  <h1>Relatorio financeiro</h1>
  <p>Projeto: ${escapeHtml(filters.project)}</p>
  <p>Periodo: ${escapeHtml(period)} | Regime: ${escapeHtml(regimeLabel)}</p>
  <p>Gerado em: ${escapeHtml(generatedAt)}</p>

  <h2>Resumo</h2>
  <table>
    ${tableHead(['Indicador', 'Valor'])}
    <tbody>${summaryRows}</tbody>
  </table>

  <h2>Despesas lancadas</h2>
  <table>
    ${tableHead(['Emissao', 'Fornecedor', 'Descricao', 'Projeto', 'Linha orcamentaria', 'Vencimento', 'Valor', 'Pagamento', 'Conta', 'Status', 'Documento', 'Observacoes'])}
    <tbody>${expenseRows}</tbody>
  </table>

  <h2>Execucao por projeto</h2>
  <table>
    ${tableHead(['Projeto', 'Orcado', 'Comprometido', 'Pago', 'Em aberto', 'Saldo'])}
    <tbody>${projectRows}</tbody>
  </table>

  <h2>Execucao por linha</h2>
  <table>
    ${tableHead(['Projeto', 'Linha', 'Orcado', 'Comprometido', 'Pago', 'Saldo'])}
    <tbody>${budgetLineRows}</tbody>
  </table>
</body>
</html>`;
}

export function FinancePage() {
  const createProject = useProjectStore(state => state.createProject);
  const [projects, setProjects] = useState<Project[]>([]);
  const [expenses, setExpenses] = useState<FinanceExpense[]>([]);
  const [suppliers, setSuppliers] = useState<FinanceSupplier[]>([]);
  const [paymentAccounts, setPaymentAccounts] = useState<PaymentAccount[]>([]);
  const [budgetLines, setBudgetLines] = useState<BudgetLine[]>([]);
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingExpense, setSavingExpense] = useState(false);
  const [savingBudgetLine, setSavingBudgetLine] = useState(false);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [isBudgetLineModalOpen, setIsBudgetLineModalOpen] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);
  const [deletingExpenseId, setDeletingExpenseId] = useState<string | null>(null);
  const [editingBudgetLineId, setEditingBudgetLineId] = useState<string | null>(null);
  const [deletingBudgetLineId, setDeletingBudgetLineId] = useState<string | null>(null);
  const [regime, setRegime] = useState<FinanceRegime>('accrual');
  const [projectId, setProjectId] = useState('');
  const [startDate, setStartDate] = useState(monthStart);
  const [endDate, setEndDate] = useState(today);
  const [searchQuery, setSearchQuery] = useState('');
  const [expenseForm, setExpenseForm] = useState<ExpenseFormData>(emptyExpenseForm());
  const [budgetLineForm, setBudgetLineForm] = useState({ project_id: '', name: '', planned_amount: '' });
  const [expensePage, setExpensePage] = useState(1);
  const [budgetLinePage, setBudgetLinePage] = useState(1);
  const [executionPage, setExecutionPage] = useState(1);
  const [expensePageSize, setExpensePageSize] = useState(DEFAULT_PAGE_SIZE);
  const [budgetLinePageSize, setBudgetLinePageSize] = useState(DEFAULT_PAGE_SIZE);
  const [executionPageSize, setExecutionPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);

  const selectedProjectLines = useMemo(
    () => budgetLines.filter(line => !expenseForm.project_id || line.project_id === expenseForm.project_id),
    [budgetLines, expenseForm.project_id]
  );

  const budgetLineById = useMemo(() => new Map(budgetLines.map(line => [line.id, line])), [budgetLines]);

  const filteredExpenses = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return expenses;

    return expenses.filter(expense => {
      const haystack = [
        expense.supplier_name,
        expense.description,
        toNumber(expense.amount),
        formatBRLCurrency(expense.amount),
        expense.payment_account_name || '',
        displayDate(expense.paid_at),
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [expenses, searchQuery]);

  const pagedExpenses = useMemo(
    () => filteredExpenses.slice((expensePage - 1) * expensePageSize, expensePage * expensePageSize),
    [filteredExpenses, expensePage, expensePageSize]
  );

  const executionLines = useMemo(() => summary?.byBudgetLine || [], [summary]);
  const pagedExecutionLines = useMemo(
    () => executionLines.slice((executionPage - 1) * executionPageSize, executionPage * executionPageSize),
    [executionLines, executionPage, executionPageSize]
  );

  const pagedBudgetLines = useMemo(
    () => budgetLines.slice((budgetLinePage - 1) * budgetLinePageSize, budgetLinePage * budgetLinePageSize),
    [budgetLines, budgetLinePage, budgetLinePageSize]
  );

  useEffect(() => {
    setExpensePage(1);
  }, [filteredExpenses]);

  useEffect(() => {
    setExecutionPage(1);
  }, [executionLines]);

  useEffect(() => {
    setBudgetLinePage(1);
  }, [budgetLines]);

  const handleExpensePageSizeChange = useCallback((size: number) => {
    setExpensePageSize(size);
    setExpensePage(1);
  }, []);

  const handleExecutionPageSizeChange = useCallback((size: number) => {
    setExecutionPageSize(size);
    setExecutionPage(1);
  }, []);

  const handleBudgetLinePageSizeChange = useCallback((size: number) => {
    setBudgetLinePageSize(size);
    setBudgetLinePage(1);
  }, []);

  const handleCreateProject = async (data: Partial<Project>, addedMemberIds?: string[]) => {
    try {
      const newProject = await createProject(data, addedMemberIds);
      setProjects(prev => [newProject, ...prev]);
      setIsCreateProjectModalOpen(false);
      toast.success('Projeto criado!');
    } catch {
      toast.error('Falha ao criar projeto');
    }
  };

  const defaultProjectId = projectId || projects[0]?.id || '';
  const selectedProject = useMemo(
    () => projects.find(project => project.id === projectId),
    [projectId, projects]
  );
  const reportProjectLabel = selectedProject
    ? `${selectedProject.management_code ? `${selectedProject.management_code} - ` : ''}${selectedProject.name}`
    : 'Todos os projetos';

  const buildReportHtml = useCallback(() => buildFinanceReportHtml({
    expenses,
    summary,
    filters: {
      project: reportProjectLabel,
      regime,
      startDate,
      endDate,
    },
    generatedAt: new Date().toLocaleString('pt-BR'),
  }), [endDate, expenses, regime, reportProjectLabel, startDate, summary]);

  const handleExportPdf = useCallback(() => {
    const reportWindow = window.open('', '_blank');
    if (!reportWindow) {
      toast.error('Permita pop-ups para gerar o PDF');
      return;
    }

    reportWindow.document.open();
    reportWindow.document.write(buildReportHtml());
    reportWindow.document.close();
    reportWindow.focus();
    window.setTimeout(() => {
      reportWindow.print();
    }, 250);
  }, [buildReportHtml]);

  const handleExportExcel = useCallback(() => {
    const blob = new Blob([`\ufeff${buildReportHtml()}`], {
      type: 'application/vnd.ms-excel;charset=utf-8;',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-financeiro-${fileDate(startDate)}-${fileDate(endDate)}.xls`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Relatorio Excel gerado');
  }, [buildReportHtml, endDate, startDate]);

  const loadReferenceData = useCallback(async () => {
    const [projectRes, supplierRes, accountRes] = await Promise.all([
      projectsApi.getAllForSelector(),
      financeApi.getSuppliers(),
      financeApi.getPaymentAccounts(),
    ]);
    setProjects(projectRes.data);
    setSuppliers(supplierRes.data);
    setPaymentAccounts(accountRes.data);
  }, []);

  const loadFinanceData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        projectId: projectId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        regime,
      };
      const [expenseRes, summaryRes, linesRes] = await Promise.all([
        financeApi.getExpenses(params),
        financeApi.getSummary(params),
        financeApi.getBudgetLines(projectId || undefined),
      ]);
      setExpenses(expenseRes.data);
      setSummary(summaryRes.data);
      setBudgetLines(linesRes.data);
    } catch {
      toast.error('Falha ao carregar financeiro');
    } finally {
      setLoading(false);
    }
  }, [endDate, projectId, regime, startDate]);

  useEffect(() => {
    loadReferenceData().catch(() => toast.error('Falha ao carregar cadastros financeiros'));
  }, [loadReferenceData]);

  useEffect(() => {
    loadFinanceData();
  }, [loadFinanceData]);

  const openExpenseModal = () => {
    setEditingExpenseId(null);
    setExpenseForm(emptyExpenseForm(defaultProjectId));
    setIsExpenseModalOpen(true);
  };

  const openEditExpenseModal = (expense: FinanceExpense) => {
    setEditingExpenseId(expense.id);
    setExpenseForm(expenseToForm(expense));
    setIsExpenseModalOpen(true);
  };

  const resetBudgetLineForm = useCallback(() => {
    setEditingBudgetLineId(null);
    setBudgetLineForm({ project_id: defaultProjectId, name: '', planned_amount: '' });
  }, [defaultProjectId]);

  const openBudgetLineModal = (line?: BudgetLine) => {
    if (line) {
      setEditingBudgetLineId(line.id);
      setBudgetLineForm({
        project_id: line.project_id,
        name: line.name,
        planned_amount: formatBRLCurrencyInput(toNumber(line.planned_amount).toFixed(2)),
      });
    } else {
      resetBudgetLineForm();
    }
    setIsBudgetLineModalOpen(true);
  };

  const handleSubmitExpense = async (event: React.FormEvent) => {
    event.preventDefault();
    const amount = parseBRLCurrency(expenseForm.amount);
    if (!amount) {
      toast.error('Informe o valor da despesa');
      return;
    }
    setSavingExpense(true);
    try {
      const payload = {
        ...expenseForm,
        budget_line_id: expenseForm.budget_line_id || null,
        due_at: expenseForm.due_at || null,
        paid_at: expenseForm.paid_at || null,
        // Fornecedor e conta são dirigidos pelo nome digitado; forçamos a
        // resolução por nome enviando os ids como null.
        supplier_id: null,
        payment_account_name: expenseForm.payment_account_name || null,
        payment_account_id: null,
        amount,
      };
      if (editingExpenseId) {
        await financeApi.updateExpense(editingExpenseId, payload);
      } else {
        await financeApi.createExpense(payload);
      }
      setIsExpenseModalOpen(false);
      toast.success(editingExpenseId ? 'Despesa atualizada' : 'Despesa lançada');
      await Promise.all([loadReferenceData(), loadFinanceData()]);
    } catch {
      toast.error(editingExpenseId ? 'Falha ao atualizar despesa' : 'Falha ao lançar despesa');
    } finally {
      setSavingExpense(false);
    }
  };

  const handleDeleteExpense = async (expense: FinanceExpense) => {
    const confirmed = window.confirm(
      `Excluir a despesa "${expense.description}" (${formatBRLCurrency(expense.amount)})? Esta ação não pode ser desfeita.`
    );
    if (!confirmed) return;
    setDeletingExpenseId(expense.id);
    try {
      await financeApi.deleteExpense(expense.id);
      toast.success('Despesa excluída');
      await Promise.all([loadReferenceData(), loadFinanceData()]);
    } catch {
      toast.error('Falha ao excluir despesa');
    } finally {
      setDeletingExpenseId(null);
    }
  };

  const handleSubmitBudgetLine = async (event: React.FormEvent) => {
    event.preventDefault();
    const plannedAmount = parseBRLCurrency(budgetLineForm.planned_amount) || 0;
    setSavingBudgetLine(true);
    try {
      if (editingBudgetLineId) {
        await financeApi.updateBudgetLine(editingBudgetLineId, {
          name: budgetLineForm.name,
          planned_amount: plannedAmount,
        });
      } else {
        await financeApi.createBudgetLine({
          project_id: budgetLineForm.project_id,
          name: budgetLineForm.name,
          planned_amount: plannedAmount,
        });
      }
      resetBudgetLineForm();
      toast.success(editingBudgetLineId ? 'Linha orçamentária atualizada' : 'Linha orçamentária salva');
      await loadFinanceData();
    } catch {
      toast.error(editingBudgetLineId ? 'Falha ao atualizar linha orçamentária' : 'Falha ao salvar linha orçamentária');
    } finally {
      setSavingBudgetLine(false);
    }
  };

  const handleDeleteBudgetLine = async (line: BudgetLine) => {
    const confirmed = window.confirm(
      `Excluir a linha orçamentária "${line.name}"? Despesas já lançadas nela permanecerão, mas ela deixará de aparecer nos cadastros.`
    );
    if (!confirmed) return;
    setDeletingBudgetLineId(line.id);
    try {
      await financeApi.deleteBudgetLine(line.id);
      toast.success('Linha orçamentária excluída');
      if (editingBudgetLineId === line.id) resetBudgetLineForm();
      await loadFinanceData();
    } catch {
      toast.error('Falha ao excluir linha orçamentária');
    } finally {
      setDeletingBudgetLineId(null);
    }
  };

  const totals = summary?.totals;
  const planned = toNumber(totals?.planned_amount);
  const total = toNumber(totals?.total_amount);
  const paid = toNumber(totals?.paid_amount);
  const open = toNumber(totals?.open_amount);
  const overdue = toNumber(totals?.overdue_amount);

  return (
    <div>
      <Header title="Financeiro" />
      <div className="p-6">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
          <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-4">
            <div className="text-sm font-medium text-[#323338]">
              <label htmlFor="finance-project-filter">Projeto</label>
              <div className="mt-1 flex items-center gap-2">
                <select
                  id="finance-project-filter"
                  value={projectId}
                  onChange={event => setProjectId(event.target.value)}
                  className="h-10 w-full rounded border border-[#D0D4E4] bg-white px-3 text-sm focus:border-[#C24914] focus:outline-none"
                >
                  <option value="">Todos os projetos</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>{project.management_code ? `${project.management_code} - ` : ''}{project.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setIsCreateProjectModalOpen(true)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-[#C24914] text-white transition-colors hover:bg-[#A83D10]"
                  title="Adicionar novo projeto"
                  aria-label="Adicionar novo projeto"
                >
                  <Plus size={18} />
                </button>
              </div>
            </div>
            <label className="text-sm font-medium text-[#323338]">
              Regime
              <select
                value={regime}
                onChange={event => setRegime(event.target.value as FinanceRegime)}
                className="mt-1 h-10 w-full rounded border border-[#D0D4E4] bg-white px-3 text-sm focus:border-[#C24914] focus:outline-none"
              >
                <option value="accrual">Competência</option>
                <option value="cash">Caixa</option>
              </select>
            </label>
            <label className="text-sm font-medium text-[#323338]">
              Início
              <input
                type="date"
                value={startDate}
                onChange={event => setStartDate(event.target.value)}
                className="mt-1 h-10 w-full rounded border border-[#D0D4E4] bg-white px-3 text-sm focus:border-[#C24914] focus:outline-none"
              />
            </label>
            <label className="text-sm font-medium text-[#323338]">
              Fim
              <input
                type="date"
                value={endDate}
                onChange={event => setEndDate(event.target.value)}
                className="mt-1 h-10 w-full rounded border border-[#D0D4E4] bg-white px-3 text-sm focus:border-[#C24914] focus:outline-none"
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" icon={<WalletCards size={16} />} onClick={() => openBudgetLineModal()} disabled={projects.length === 0}>
              Linhas orçamentárias
            </Button>
            <Button type="button" icon={<Plus size={16} />} onClick={openExpenseModal} disabled={projects.length === 0}>
              Nova despesa
            </Button>
          </div>
        </div>

        <section className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded border border-[#D0D4E4] bg-white px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-[#323338]">Relatórios financeiros</h2>
            <p className="mt-1 text-xs text-[#676879]">Gere o relatório com os filtros atuais de projeto, período e regime.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" icon={<FileText size={16} />} onClick={handleExportPdf} disabled={loading || !summary}>
              Gerar PDF
            </Button>
            <Button type="button" variant="secondary" icon={<Sheet size={16} />} onClick={handleExportExcel} disabled={loading || !summary}>
              Exportar Excel
            </Button>
          </div>
        </section>

        <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Metric title="Orçado" value={planned} icon={<WalletCards size={18} />} />
          <Metric title={regime === 'cash' ? 'Saídas no caixa' : 'Comprometido'} value={total} icon={<ReceiptText size={18} />} />
          <Metric title="Pago" value={paid} icon={<DollarSign size={18} />} />
          <Metric title="Em aberto" value={open} icon={<CalendarDays size={18} />} />
          <Metric title="Atrasado" value={overdue} icon={<AlertCircle size={18} />} tone="danger" />
          <Metric title="Saldo previsto" value={planned - total} icon={<DollarSign size={18} />} tone={planned - total < 0 ? 'danger' : 'default'} />
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 xl:grid-cols-[2fr_1fr]">
          <section className="rounded border border-[#D0D4E4] bg-white">
            <div className="flex items-center justify-between border-b border-[#D0D4E4] px-4 py-3">
              <h2 className="text-sm font-semibold text-[#323338]">Despesas lançadas</h2>
              <span className="text-xs text-[#676879]">{filteredExpenses.length} de {expenses.length} registro(s)</span>
            </div>
            <div className="border-b border-[#D0D4E4] bg-[#F7F9FF] px-4 py-3">
              <div className="relative">
                <Search size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#C24914]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  placeholder="Buscar por fornecedor, descrição, valor ou pagamento"
                  className="h-11 w-full rounded-lg border-2 border-[#edc6b5] bg-white pl-10 pr-10 text-sm font-medium text-[#323338] shadow-sm placeholder:font-normal placeholder:text-[#8C91A3] focus:border-[#C24914] focus:outline-none focus:ring-2 focus:ring-[#edc6b5]"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C91A3] transition-colors hover:text-[#323338]"
                    title="Limpar busca"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#C24914] border-t-transparent" />
              </div>
            ) : filteredExpenses.length === 0 ? (
              <div className="py-12 text-center text-sm text-[#676879]">
                {expenses.length === 0 ? 'Nenhuma despesa encontrada para os filtros atuais.' : 'Nenhuma despesa encontrada para a pesquisa atual.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#D0D4E4] text-sm">
                  <thead className="bg-[#F7F9FF] text-left text-xs font-semibold uppercase text-[#676879]">
                    <tr>
                      <th className="px-4 py-3">Emissão</th>
                      <th className="px-4 py-3">Fornecedor / descrição</th>
                      <th className="px-4 py-3">Projeto</th>
                      <th className="px-4 py-3">Vencimento</th>
                      <th className="px-4 py-3 text-right">Valor</th>
                      <th className="px-4 py-3">Pagamento</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#EEF1F7]">
                    {pagedExpenses.map(expense => (
                      <tr key={expense.id} className="hover:bg-[#F7F9FF]">
                        <td className="whitespace-nowrap px-4 py-3 text-[#323338]">{displayDate(expense.issued_at)}</td>
                        <td className="max-w-md px-4 py-3">
                          <p className="font-semibold text-[#323338]">{expense.supplier_name}</p>
                          <p className="mt-0.5 line-clamp-2 text-xs text-[#676879]">{expense.description}</p>
                          {expense.budget_line_name && <p className="mt-1 text-xs text-[#C24914]">{expense.budget_line_name}</p>}
                        </td>
                        <td className="px-4 py-3 text-[#323338]">
                          <div className="flex items-center gap-2">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: expense.project_color || '#C24914' }} />
                            <span>{expense.management_code ? `${expense.management_code} - ` : ''}{expense.project_name}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-[#676879]">{displayDate(expense.due_at)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-[#323338]">{formatBRLCurrency(expense.amount)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-[#676879]">{displayDate(expense.paid_at)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusStyles[expense.status]}`}>
                            {statusLabels[expense.status]}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => openEditExpenseModal(expense)}
                              className="rounded p-1.5 text-[#676879] transition-colors hover:bg-[#f9f3f0] hover:text-[#C24914]"
                              title="Editar despesa"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteExpense(expense)}
                              disabled={deletingExpenseId === expense.id}
                              className="rounded p-1.5 text-[#676879] transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                              title="Excluir despesa"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination page={expensePage} pageSize={expensePageSize} totalItems={filteredExpenses.length} onPageChange={setExpensePage} onPageSizeChange={handleExpensePageSizeChange} />
          </section>

          <section className="rounded border border-[#D0D4E4] bg-white">
            <div className="flex items-center justify-between border-b border-[#D0D4E4] px-4 py-3">
              <h2 className="text-sm font-semibold text-[#323338]">Execução por linha</h2>
              <button
                type="button"
                onClick={() => openBudgetLineModal()}
                className="text-xs font-medium text-[#C24914] hover:underline"
              >
                Gerenciar linhas
              </button>
            </div>
            <div className="max-h-[520px] divide-y divide-[#EEF1F7] overflow-y-auto">
              {pagedExecutionLines.map(line => {
                const plannedAmount = toNumber(line.planned_amount);
                const totalAmount = toNumber(line.total_amount);
                const percent = plannedAmount > 0 ? Math.min(100, Math.round((totalAmount / plannedAmount) * 100)) : 0;
                const fullLine = budgetLineById.get(line.budget_line_id);
                return (
                  <div key={line.budget_line_id} className="px-4 py-3">
                    <div className="mb-1 flex items-center justify-between gap-2 text-sm">
                      <span className="font-medium text-[#323338]">{line.budget_line_name}</span>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-[#676879]">{percent}%</span>
                        {fullLine && (
                          <>
                            <button
                              type="button"
                              onClick={() => openBudgetLineModal(fullLine)}
                              className="rounded p-1 text-[#676879] transition-colors hover:bg-[#f9f3f0] hover:text-[#C24914]"
                              title="Editar linha orçamentária"
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteBudgetLine(fullLine)}
                              disabled={deletingBudgetLineId === fullLine.id}
                              className="rounded p-1 text-[#676879] transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                              title="Excluir linha orçamentária"
                            >
                              <Trash2 size={13} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <p className="mb-2 text-xs text-[#676879]">{line.project_name}</p>
                    <div className="h-2 overflow-hidden rounded-full bg-[#EEF1F7]">
                      <div className="h-full rounded-full bg-[#C24914]" style={{ width: `${percent}%` }} />
                    </div>
                    <div className="mt-2 flex justify-between text-xs text-[#676879]">
                      <span>{formatBRLCurrency(totalAmount)}</span>
                      <span>{formatBRLCurrency(plannedAmount)}</span>
                    </div>
                  </div>
                );
              })}
              {!loading && executionLines.length === 0 && (
                <div className="px-4 py-10 text-center text-sm text-[#676879]">Nenhuma linha orçamentária cadastrada.</div>
              )}
            </div>
            <Pagination page={executionPage} pageSize={executionPageSize} totalItems={executionLines.length} onPageChange={setExecutionPage} onPageSizeChange={handleExecutionPageSizeChange} />
          </section>
        </div>
      </div>

      <Modal isOpen={isExpenseModalOpen} onClose={() => setIsExpenseModalOpen(false)} title={editingExpenseId ? 'Editar despesa' : 'Nova despesa'} size="xl">
        <form onSubmit={handleSubmitExpense} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Projeto *">
              <select required value={expenseForm.project_id} onChange={event => setExpenseForm(prev => ({ ...prev, project_id: event.target.value, budget_line_id: '' }))} className="input-field">
                <option value="">Selecione</option>
                {projects.map(project => <option key={project.id} value={project.id}>{project.management_code ? `${project.management_code} - ` : ''}{project.name}</option>)}
              </select>
            </Field>
            <Field label="Linha orçamentária">
              <select value={expenseForm.budget_line_id} onChange={event => setExpenseForm(prev => ({ ...prev, budget_line_id: event.target.value }))} className="input-field">
                <option value="">Sem linha</option>
                {selectedProjectLines.map(line => <option key={line.id} value={line.id}>{line.name}</option>)}
              </select>
            </Field>
            <Field label="Data de emissão *">
              <input required type="date" value={expenseForm.issued_at} onChange={event => setExpenseForm(prev => ({ ...prev, issued_at: event.target.value }))} className="input-field" />
            </Field>
            <Field label="Tipo de documento *">
              <input required value={expenseForm.document_type} onChange={event => setExpenseForm(prev => ({ ...prev, document_type: event.target.value }))} className="input-field" placeholder="Fatura, NF, Recibo..." />
            </Field>
            <Field label="Fornecedor *">
              <input required list="finance-suppliers" value={expenseForm.supplier_name} onChange={event => setExpenseForm(prev => ({ ...prev, supplier_name: event.target.value }))} className="input-field" />
              <datalist id="finance-suppliers">{suppliers.map(supplier => <option key={supplier.id} value={supplier.name} />)}</datalist>
            </Field>
            <Field label="Valor *">
              <input required value={expenseForm.amount} onChange={event => setExpenseForm(prev => ({ ...prev, amount: formatBRLCurrencyInput(event.target.value) }))} className="input-field" placeholder="R$ 0,00" />
            </Field>
            <Field label="Vencimento">
              <input type="date" value={expenseForm.due_at} onChange={event => setExpenseForm(prev => ({ ...prev, due_at: event.target.value }))} className="input-field" />
            </Field>
            <Field label="Data de pagamento">
              <input type="date" value={expenseForm.paid_at} onChange={event => setExpenseForm(prev => ({ ...prev, paid_at: event.target.value, status: event.target.value ? 'paid' : prev.status }))} className="input-field" />
            </Field>
            <Field label="Conta para pagamento">
              <input list="payment-accounts" value={expenseForm.payment_account_name} onChange={event => setExpenseForm(prev => ({ ...prev, payment_account_name: event.target.value }))} className="input-field" placeholder="Banco, cartão, fatura..." />
              <datalist id="payment-accounts">{paymentAccounts.map(account => <option key={account.id} value={account.name} />)}</datalist>
            </Field>
            <Field label="Status">
              <select value={expenseForm.status} onChange={event => setExpenseForm(prev => ({ ...prev, status: event.target.value as ExpenseStatus }))} className="input-field">
                <option value="open">Em aberto</option>
                <option value="paid">Pago</option>
                <option value="cancelled">Cancelado</option>
              </select>
            </Field>
          </div>
          <Field label="Descrição *">
            <textarea required value={expenseForm.description} onChange={event => setExpenseForm(prev => ({ ...prev, description: event.target.value }))} className="input-field min-h-[88px] resize-none" />
          </Field>
          <Field label="Observações">
            <textarea value={expenseForm.notes} onChange={event => setExpenseForm(prev => ({ ...prev, notes: event.target.value }))} className="input-field min-h-[72px] resize-none" />
          </Field>
          <div className="rounded border border-[#D0D4E4] bg-white">
            <div className="flex items-center gap-2 border-b border-[#D0D4E4] px-4 py-3 text-sm font-semibold text-[#323338]">
              <Paperclip size={16} />
              Arquivos
            </div>
            <AttachmentPanel
              ownerId={editingExpenseId || undefined}
              kind="expense"
              disabledTitle="Salve a despesa primeiro"
              disabledText="Depois de salvar, reabra a despesa para anexar comprovantes, notas e recibos."
              emptyTitle="Nenhum arquivo anexado"
              emptyText="Anexe comprovantes, notas fiscais, boletos ou recibos deste lancamento."
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setIsExpenseModalOpen(false)} className="flex-1 justify-center">Cancelar</Button>
            <Button type="submit" loading={savingExpense} className="flex-1 justify-center">{editingExpenseId ? 'Salvar alterações' : 'Salvar despesa'}</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isBudgetLineModalOpen} onClose={() => setIsBudgetLineModalOpen(false)} title="Linhas orçamentárias" size="xl">
        <div className="space-y-5">
          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-[#323338]">Linhas cadastradas</h3>
                <p className="text-xs text-[#676879]">{reportProjectLabel}</p>
              </div>
              <span className="text-xs text-[#676879]">{budgetLines.length} linha(s)</span>
            </div>
            <div className="max-h-64 divide-y divide-[#EEF1F7] overflow-y-auto rounded border border-[#D0D4E4]">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#C24914] border-t-transparent" />
                </div>
              ) : budgetLines.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-[#676879]">
                  Nenhuma linha orçamentária cadastrada{projectId ? ' para este projeto' : ''}. Use o formulário abaixo para criar a primeira.
                </div>
              ) : (
                pagedBudgetLines.map(line => {
                  const plannedAmt = toNumber(line.planned_amount);
                  const spentAmt = toNumber(line.spent_amount);
                  const percent = plannedAmt > 0 ? Math.min(100, Math.round((spentAmt / plannedAmt) * 100)) : 0;
                  return (
                    <div key={line.id} className={`px-3 py-2.5 ${editingBudgetLineId === line.id ? 'bg-[#f9f3f0]' : ''}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-[#323338]">{line.name}</p>
                          {!projectId && <p className="truncate text-xs text-[#676879]">{line.project_name}</p>}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          <span className="whitespace-nowrap text-xs text-[#676879]">
                            {formatBRLCurrency(spentAmt)} / {formatBRLCurrency(plannedAmt)}
                          </span>
                          <button
                            type="button"
                            onClick={() => openBudgetLineModal(line)}
                            className="rounded p-1.5 text-[#676879] transition-colors hover:bg-[#f9f3f0] hover:text-[#C24914]"
                            title="Editar linha orçamentária"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteBudgetLine(line)}
                            disabled={deletingBudgetLineId === line.id}
                            className="rounded p-1.5 text-[#676879] transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                            title="Excluir linha orçamentária"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#EEF1F7]">
                        <div className="h-full rounded-full bg-[#C24914]" style={{ width: `${percent}%` }} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <Pagination page={budgetLinePage} pageSize={budgetLinePageSize} totalItems={budgetLines.length} onPageChange={setBudgetLinePage} onPageSizeChange={handleBudgetLinePageSizeChange} />
          </div>

          <div className="border-t border-[#D0D4E4] pt-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#323338]">
                {editingBudgetLineId ? `Editando: ${budgetLineForm.name || 'linha orçamentária'}` : 'Nova linha orçamentária'}
              </h3>
              {editingBudgetLineId && (
                <button type="button" onClick={resetBudgetLineForm} className="text-xs font-medium text-[#C24914] hover:underline">
                  Cancelar edição
                </button>
              )}
            </div>
            <form onSubmit={handleSubmitBudgetLine} className="space-y-4">
              <Field label="Projeto *">
                <select
                  required
                  disabled={Boolean(editingBudgetLineId)}
                  value={budgetLineForm.project_id}
                  onChange={event => setBudgetLineForm(prev => ({ ...prev, project_id: event.target.value }))}
                  className="input-field disabled:bg-[#F7F9FF] disabled:text-[#676879]"
                >
                  <option value="">Selecione</option>
                  {projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
                </select>
              </Field>
              <Field label="Nome *">
                <input required value={budgetLineForm.name} onChange={event => setBudgetLineForm(prev => ({ ...prev, name: event.target.value }))} className="input-field" placeholder="Ex.: Energia, Passagens, Aluguel" />
              </Field>
              <Field label="Valor orçado">
                <input value={budgetLineForm.planned_amount} onChange={event => setBudgetLineForm(prev => ({ ...prev, planned_amount: formatBRLCurrencyInput(event.target.value) }))} className="input-field" placeholder="R$ 0,00" />
              </Field>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="secondary" onClick={() => setIsBudgetLineModalOpen(false)} className="flex-1 justify-center">Fechar</Button>
                <Button type="submit" loading={savingBudgetLine} className="flex-1 justify-center">{editingBudgetLineId ? 'Salvar alterações' : 'Salvar linha'}</Button>
              </div>
            </form>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isCreateProjectModalOpen} onClose={() => setIsCreateProjectModalOpen(false)} title="Novo Projeto" size="lg">
        <ProjectForm onSubmit={handleCreateProject} onCancel={() => setIsCreateProjectModalOpen(false)} />
      </Modal>
    </div>
  );
}

function Metric({ title, value, icon, tone = 'default' }: { title: string; value: number; icon: React.ReactNode; tone?: 'default' | 'danger' }) {
  return (
    <div className="rounded border border-[#D0D4E4] bg-white p-4">
      <div className="mb-3 flex items-center justify-between text-[#676879]">
        <span className="text-xs font-semibold uppercase">{title}</span>
        {icon}
      </div>
      <p className={`text-xl font-bold ${tone === 'danger' ? 'text-[#E2445C]' : 'text-[#323338]'}`}>{formatBRLCurrency(value)}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm font-medium text-[#323338]">
      <span className="mb-1 block">{label}</span>
      {children}
    </label>
  );
}
